import * as types from '../types';
import { IPortalRolloutApi } from '../contracts';
import { HttpApi } from './HttpApi';
import { withJsonErrorBody } from './attachments';
import { filenameFromDisposition } from './importExport';

const enc = encodeURIComponent;
const base = '/portal-rollout';

/** Only the filters that are set go on the query string (the server rejects blanks). */
function params(query: Record<string, string | number | undefined>): Record<string, string | number> {
  const result: Record<string, string | number> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    result[key] = value;
  });
  return result;
}

/** Portal rollout campaigns (tracker item 81). */
export class PortalRolloutHttpApi extends HttpApi implements IPortalRolloutApi {
  private async get<T>(url: string, query?: Record<string, string | number | undefined>): Promise<T> {
    return (await this.http.get<types.APIResponse<T>>(`${base}${url}`, query ? { params: params(query) } : undefined)).data.data;
  }

  private async post<T>(url: string, body: unknown = {}): Promise<T> {
    return (await this.http.post<types.APIResponse<T>>(`${base}${url}`, body)).data.data;
  }

  getPortalRolloutReadiness() {
    return this.get<types.RolloutReadiness>('/readiness');
  }

  previewPortalRollout(input: types.RolloutCampaignInput) {
    return this.post<types.RolloutPreview>('/campaigns/preview', input);
  }

  createPortalRolloutCampaign(input: types.RolloutCampaignInput) {
    return this.post<types.RolloutCampaign>('/campaigns', input);
  }

  listPortalRolloutCampaigns(query: types.RolloutCampaignListQuery = {}) {
    return this.get<types.RolloutPage<types.RolloutCampaign>>('/campaigns', { ...query });
  }

  getPortalRolloutCampaign(id: string, query: types.RolloutRecipientQuery = {}) {
    return this.get<types.RolloutCampaignDetail>(`/campaigns/${enc(id)}`, { ...query });
  }

  startPortalRolloutCampaign(id: string) {
    return this.post<types.RolloutCampaign>(`/campaigns/${enc(id)}/start`);
  }

  pausePortalRolloutCampaign(id: string) {
    return this.post<types.RolloutCampaign>(`/campaigns/${enc(id)}/pause`);
  }

  resumePortalRolloutCampaign(id: string) {
    return this.post<types.RolloutCampaign>(`/campaigns/${enc(id)}/resume`);
  }

  cancelPortalRolloutCampaign(id: string) {
    return this.post<types.RolloutCampaign>(`/campaigns/${enc(id)}/cancel`);
  }

  sendPortalRolloutBatch(id: string) {
    return this.post<types.RolloutBatchResult>(`/campaigns/${enc(id)}/send-batch`);
  }

  resendPortalRolloutCampaign(id: string) {
    return this.post<types.RolloutResendResult>(`/campaigns/${enc(id)}/resend`);
  }

  resendPortalRolloutRecipient(id: string, recipientId: string) {
    return this.post<types.RolloutRecipientResendResult>(`/campaigns/${enc(id)}/recipients/${enc(recipientId)}/resend`);
  }

  async exportPortalRolloutRecipients(id: string): Promise<types.CsvDownload> {
    try {
      const response = await this.http.get<Blob>(`${base}/campaigns/${enc(id)}/export`, { responseType: 'blob', timeout: 120_000 });
      return { blob: response.data, filename: filenameFromDisposition(response.headers?.['content-disposition'], 'portal-rollout-recipients.csv') };
    } catch (error) {
      throw await withJsonErrorBody(error);
    }
  }

  getCustomerPortalRollout(customerId: string) {
    return this.get<types.CustomerRollout>(`/customers/${enc(customerId)}`);
  }
}
